import { invokeFunction } from '@/lib/supabase';

export interface ProvisioningResult {
  user_id: string;
  household_id: string;
  inbox_address: string;
  already_provisioned: boolean;
  invitation_id?: string;
  /**
   * The invite token we sent was stale/revoked, so the server provisioned the
   * account the ordinary way instead of failing. The caller should drop the
   * stored token so it is not replayed on every reload.
   */
  invitation_unavailable?: boolean;
}

export async function provisionUser(
  invitationToken?: string | null,
): Promise<ProvisioningResult> {
  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  const { data, error } = await invokeFunction('users-provision', {
    method: 'POST',
    body: {
      timezone,
      // YAR-438: this build creates the household from the onboarding fork
      // (households-create) — provisioning must not create one at sign-in.
      // A bundle that predates the fork omits this and keeps the old path.
      client_creates_household: true,
      ...(invitationToken ? { invitation_token: invitationToken } : {}),
    },
  });

  if (error) {
    throw new Error(`User provisioning failed: ${error.message}`);
  }

  if (!data?.success) {
    throw new Error(data?.error ?? 'Unknown provisioning error');
  }

  return {
    user_id: data.user_id,
    household_id: data.household_id,
    inbox_address: data.inbox_address,
    already_provisioned: data.already_provisioned,
    invitation_id: data.invitation_id,
    invitation_unavailable: data.invitation_unavailable === true,
  };
}
